import { useRef, useState } from 'react';
import PageShell from '@components/pages/PageShell';
import {
    ResourceHero,
    ArticleCard,
    Pagination,
    SectionLabel,
    TopAndRecent,
    EmptyList,
} from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import { ARTICLES, TOP_ARTICLE, RECENT_RAIL } from '@/data/resources';
import { usePagedList } from '@/lib/usePagedList';

const BASE = '/resources/blog';

/** Three rows of the four-column grid. */
const PER_PAGE = 12;

export default function ResourcesBlog() {
    const [query, setQuery] = useState('');
    const grid = useRef<HTMLElement>(null);
    const { page, setPage, pages, matches, pageItems } = usePagedList(
        ARTICLES,
        PER_PAGE,
        query,
        (a) => `${a.title} ${a.excerpt ?? ''} ${a.tags?.join(' ') ?? ''} ${a.body?.join(' ') ?? ''}`,
    );
    const searching = query.trim().length > 0;

    const goTo = (next: number) => {
        setPage(next);
        grid.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <PageShell>
            <ResourceHero
                titleLead="Our"
                titleBold="Blog"
                intro="Stories, analysis and field notes from the work of making justice accessible, affordable and understandable for everyone."
                query={query}
                onSearch={setQuery}
                searchPlaceholder="Search articles"
            />

            {!searching && TOP_ARTICLE.title && <TopAndRecent top={TOP_ARTICLE} recent={RECENT_RAIL} />}

            <section ref={grid} className="scroll-mt-24 bg-white pb-14 pt-10 lg:pb-20" aria-live="polite">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <SectionLabel>{searching ? 'Search results' : 'Latest Articles'}</SectionLabel>
                        <p className="text-[13px] text-[#5e5e5e]">
                            {searching
                                ? `${matches.length} ${matches.length === 1 ? 'article' : 'articles'} for “${query.trim()}”`
                                : `${ARTICLES.length} articles`}
                        </p>
                    </div>

                    {pageItems.length > 0 ? (
                        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                            {pageItems.map((a) => (
                                <ArticleCard key={a.slug} item={a} basePath={BASE} />
                            ))}
                        </div>
                    ) : (
                        <EmptyList query={query} whenSearching={`No articles match “${query.trim()}”.`} />
                    )}

                    <Pagination page={page} pages={pages} onChange={goTo} />
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
